/**
 * Cloudflare Turnstile, client side — one fresh token per capture.
 *
 * The widget is INVISIBLE and run on demand rather than rendered into a form. There is no form
 * here: a capture starts from a button in a Figma panel, and dropping a visible challenge into
 * that flow would charge every honest user an interaction to protect against a bot that does not
 * use the UI at all. `execute` mode runs the check when asked and only shows something if
 * Cloudflare decides the visitor needs to prove it.
 *
 * TOKENS ARE SINGLE-USE and expire after five minutes. A token held across two captures comes
 * back from siteverify as `timeout-or-duplicate` and the second capture is refused — so this
 * resets the widget after every handout and never caches. The cost is one `execute` per capture,
 * which for a pass is a round trip measured in tens of milliseconds against a capture measured in
 * seconds.
 *
 * NOTHING HERE THROWS INTO THE CAPTURE PATH. Every failure resolves to `null`:
 *
 *   - no site key configured (preview, local) — the server admits unverified captures in exactly
 *     the same case, and the two have to agree or local development breaks
 *   - the script is blocked, by a network policy or an extension
 *   - the challenge times out or errors
 *
 * A null token still sends the request. The server decides what a missing token means, because it
 * is the only side that knows whether a secret is bound — a client that refused to send would be
 * second-guessing a configuration it cannot see, and would break every preview deployment.
 */

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

/** The widget may legitimately take a moment; a capture must not wait on it forever. */
const EXECUTE_TIMEOUT_MS = 15000;
const SCRIPT_TIMEOUT_MS = 10000;

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  execute(widget: string, options?: Record<string, unknown>): void;
  reset(widget?: string): void;
  remove(widget: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

function api(): TurnstileApi | undefined {
  return typeof window === 'undefined' ? undefined : window.turnstile;
}

/** Load the Turnstile script once. Resolves false if it cannot be loaded. */
let scriptLoad: Promise<boolean> | undefined;
function loadScript(): Promise<boolean> {
  if (scriptLoad) return scriptLoad;
  scriptLoad = new Promise<boolean>((resolve) => {
    if (api()) {
      resolve(true);
      return;
    }
    if (typeof document === 'undefined') {
      resolve(false);
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    const script = (existing as HTMLScriptElement | null) ?? document.createElement('script');

    // Settle exactly once. `onerror` after a timeout, or a second load event, must not re-resolve
    // a promise that already answered — harmless here but the kind of thing that turns into a
    // double-render of the widget later.
    let settled = false;
    const settle = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(ok && Boolean(api()));
    };

    const timer = setTimeout(() => settle(false), SCRIPT_TIMEOUT_MS);
    script.addEventListener('load', () => settle(true));
    script.addEventListener('error', () => settle(false));

    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return scriptLoad;
}

/**
 * The hidden host element.
 *
 * Off-screen rather than `display: none`: Turnstile refuses to run in a container it considers
 * invisible, and an interactive challenge — the case this exists to support — has to be able to
 * appear. Positioned off-canvas, it can be promoted by Cloudflare's own overlay when needed.
 */
function container(): HTMLElement {
  const id = 'cf-turnstile-host';
  const found = document.getElementById(id);
  if (found) return found;
  const host = document.createElement('div');
  host.id = id;
  host.style.position = 'fixed';
  host.style.bottom = '0';
  host.style.left = '-10000px';
  host.style.width = '300px';
  host.style.height = '65px';
  document.body.appendChild(host);
  return host;
}

let widgetId: string | undefined;
/** Serialises execute() calls — the widget holds one pending challenge at a time. */
let inFlight: Promise<string | null> | undefined;

function renderWidget(siteKey: string): string | undefined {
  const turnstile = api();
  if (!turnstile) return undefined;
  if (widgetId) return widgetId;
  try {
    widgetId = turnstile.render(container(), {
      sitekey: siteKey,
      // Run only when asked, rather than on render. A challenge solved at page load has usually
      // expired by the time someone presses Capture.
      execution: 'execute',
      appearance: 'interaction-only',
      size: 'invisible',
      retry: 'never',
      // The panel is dark; see main.tsx, which applies `.figma-dark` unconditionally.
      theme: 'dark',
    });
    return widgetId;
  } catch {
    widgetId = undefined;
    return undefined;
  }
}

export interface TurnstileConfig {
  /** The PUBLIC site key. Empty or absent disables the client side entirely. */
  siteKey?: string;
}

/**
 * A fresh single-use token, or `null` when one cannot be obtained.
 *
 * Never rejects. See the header: the server is the side that decides what a missing token means.
 */
export async function getTurnstileToken(config: TurnstileConfig): Promise<string | null> {
  const siteKey = config.siteKey?.trim();
  if (!siteKey) return null;
  if (typeof document === 'undefined') return null;

  // One challenge at a time. Two captures started together would otherwise race on the single
  // widget, and the loser's callback would overwrite the winner's token.
  if (inFlight) {
    try {
      await inFlight;
    } catch {
      /* the previous caller already handled it */
    }
  }

  const run = (async (): Promise<string | null> => {
    if (!(await loadScript())) return null;
    const turnstile = api();
    if (!turnstile) return null;

    const id = renderWidget(siteKey);
    if (!id) return null;

    return new Promise<string | null>((resolve) => {
      let settled = false;
      const finish = (token: string | null) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        // Always reset: a consumed token is spent, and a failed challenge leaves the widget in a
        // state that will not run again.
        try {
          turnstile.reset(id);
        } catch {
          /* a widget that cannot be reset is rebuilt on the next call */
        }
        resolve(token);
      };

      const timer = setTimeout(() => finish(null), EXECUTE_TIMEOUT_MS);

      try {
        turnstile.execute(id, {
          callback: (token: string) => finish(token),
          'error-callback': () => finish(null),
          'timeout-callback': () => finish(null),
          'expired-callback': () => finish(null),
        });
      } catch {
        finish(null);
      }
    });
  })();

  inFlight = run;
  try {
    return await run;
  } finally {
    if (inFlight === run) inFlight = undefined;
  }
}

/** Drop the widget — used by tests, and after a configuration change. */
export function resetTurnstile(): void {
  const turnstile = api();
  if (turnstile && widgetId) {
    try {
      turnstile.remove(widgetId);
    } catch {
      /* already gone */
    }
  }
  widgetId = undefined;
  inFlight = undefined;
}

/** Exposed for tests; the module otherwise loads the script exactly once per page. */
export function __resetScriptLoadForTests(): void {
  scriptLoad = undefined;
  widgetId = undefined;
  inFlight = undefined;
}
